/**
 * Aba Roadmap — cronograma executivo com densidade operacional.
 */
import React, { useMemo } from 'react';
import {
  Flag,
  GanttChart,
  GraduationCap,
  ClipboardCheck,
  Eye,
  Wrench,
} from 'lucide-react';
import { ProjectRoadmap } from '../consulting/ProjectRoadmap';
import type { RoadmapItem } from '../../types/consultingProject';
import { ClickableMetric, ProjectEmptyState } from './projectHubShared';

const TYPE_LABELS: Record<RoadmapItem['type'], string> = {
  kaizen: 'Melhorias',
  milestone: 'Marcos',
  training: 'Treinamentos',
  audit: 'Auditorias',
  review: 'Revisões',
};

interface ProjectRoadmapExecutiveTabProps {
  items: RoadmapItem[];
  organizationId?: string;
  projectId?: string;
  projectName?: string;
  onAddItem: (item: Omit<RoadmapItem, 'id' | 'projectId' | 'createdAt' | 'updatedAt' | 'order'>) => void;
  onUpdateItem: (id: string, updates: Partial<RoadmapItem>) => void;
  onDeleteItem: (id: string) => void;
}

export const ProjectRoadmapExecutiveTab: React.FC<ProjectRoadmapExecutiveTabProps> = ({
  items,
  organizationId,
  projectId,
  projectName,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}) => {
  const stats = useMemo(() => {
    const now = Date.now();
    const delayed = items.filter(item => {
      if (item.status === 'completed' || item.status === 'cancelled') return false;
      return new Date(item.plannedEnd).getTime() < now;
    }).length;
    return {
      total: items.length,
      completed: items.filter(i => i.status === 'completed').length,
      inProgress: items.filter(i => i.status === 'in_progress').length,
      delayed,
      byType: Object.keys(TYPE_LABELS).map(type => ({
        type: type as RoadmapItem['type'],
        count: items.filter(i => i.type === type).length,
      })),
    };
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="p-2">
        <ProjectEmptyState
          icon={<GanttChart size={24} />}
          title="Cronograma não estruturado"
          description="O roadmap executivo ainda não possui marcos, melhorias, treinamentos ou auditorias programadas. Estruture o plano de transformação por período."
          actions={
            <button
              type="button"
              onClick={() => onAddItem({
                title: 'Diagnóstico Inicial',
                description: '',
                area: 'Geral',
                type: 'milestone',
                status: 'planned',
                plannedStart: new Date().toISOString().slice(0, 10),
                plannedEnd: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
                team: [],
              })}
              className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700"
            >
              <Flag size={12} />
              Criar primeiro marco
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-2">
      <div className="grid grid-cols-2 gap-1.5 md:grid-cols-4">
        <ClickableMetric label="Itens" value={stats.total} />
        <ClickableMetric label="Em andamento" value={stats.inProgress} />
        <ClickableMetric label="Concluídos" value={stats.completed} tone="success" />
        <ClickableMetric label="Atrasados" value={stats.delayed} tone={stats.delayed > 0 ? 'danger' : 'default'} />
      </div>

      <div className="flex flex-wrap gap-1">
        {stats.byType.filter(t => t.count > 0).map(({ type, count }) => (
          <span key={type} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-[9px] text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            {typeIcon(type)}
            {TYPE_LABELS[type]}: <strong>{count}</strong>
          </span>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <ProjectRoadmap
          items={items}
          organizationId={organizationId}
          projectId={projectId}
          projectName={projectName}
          onAddItem={onAddItem}
          onUpdateItem={onUpdateItem}
          onDeleteItem={onDeleteItem}
        />
      </div>
    </div>
  );
};

function typeIcon(type: RoadmapItem['type']) {
  const size = 10;
  switch (type) {
    case 'milestone': return <Flag size={size} />;
    case 'training': return <GraduationCap size={size} />;
    case 'audit': return <ClipboardCheck size={size} />;
    case 'review': return <Eye size={size} />;
    default: return <Wrench size={size} />;
  }
}
